import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
    FORUM_TOPIC_EDIT,
    SEO_TITLE_FORUM_TOPIC_INFO_category,
    SEO_TITLE_FORUM_TOPICS,
} from 'mk/autogenerated/translations/TopicInfoPage.327718493773a68dc011e9d194a3b43c'
import { hasAds, isAuthenticatedAs } from 'mk/bazaar/common/userUtils';
import { toastAdd } from 'mk2/actions';
import TopicInfo from 'mk2/apps/forum/containers/TopicInfo/TopicInfo';
import {
    topicInfoFetchTrigger,
    topicInviteModeratorTrigger,
    topicRemoveModeratorTrigger,
} from 'mk2/apps/forum/containers/TopicInfo/TopicInfo.actions';
import topicInfosReducer from 'mk2/apps/forum/containers/TopicInfo/TopicInfo.reducers';
import topicInfosSaga from 'mk2/apps/forum/containers/TopicInfo/TopicInfo.sagas';
import { getForumTopicInfoState } from 'mk2/apps/forum/containers/TopicInfo/TopicInfo.selectors';
import { topicModeratorInvitationDeclineTrigger } from 'mk2/apps/forum/containers/TopicModeratorInvitation/TopicModeratorInvitation.actions';
import { topicUrl } from 'mk2/apps/forum/helpers';
import {
    TopicEntity,
    TopicModeratorsDetailsEntity,
    TopicModeratorsDetailsSchema,
    TopicSchema,
} from 'mk2/apps/forum/schemas';
import { forumTopicEditUrl, forumUrl } from 'mk2/apps/forum/urls';
import { Toast } from 'mk2/components/toasts/Toast';
import { Btn, BtnType } from 'mk2/components/Btn';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { url } from 'mk2/helpers/urls';
import Page, { AnchorLeaderboardProps, BrandingProps, HalfpageProps } from 'mk2/pages/Page';
import {
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
    AppState,
} from 'mk2/reducers';
import { isSuccess, LoadingState } from 'mk2/reducers/loadingState';
import { UserEntity } from 'mk2/schemas';
import { getDenormalizedEntity } from 'mk2/selectors';
import { interpolate } from 'mk2/services/i18n';
import { Store } from 'mk2/store/configureStore';
import React from 'react';
import { MapStateToPropsParam } from 'react-redux';
import { RouteComponentProps } from 'react-router';

const fasPencilLg: FontAwesomeIconProps = {icon: faPencil, size: 'lg'};

interface OwnProps {}
interface StateProps {
    topic: TopicEntity;
    moderatorsDetails: TopicModeratorsDetailsEntity;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    loadingState: LoadingState;
    isMobile: boolean;
}

interface DispatchProps {
    onLoad(topicSlug: string);
    onInviteModerator(topicSlug: string);
    onRemoveModerator(topicSlug: string, username: string);
    onDeclineInvitation(id: number);
    onErrorToast(msg: string);
}

type RouteProps = RouteComponentProps<{
    topicSlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class TopicInfoPage extends Page<OwnProps & RouteProps, StateProps, DispatchProps> {

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.topicSlug;
            const topicState = getForumTopicInfoState(state, slug);
            const topic: TopicEntity = topicState.topicId ?
                getDenormalizedEntity<TopicEntity>(state, TopicSchema, topicState.topicId) :
                null;
            const moderatorsDetails = topicState.topicId ?
                getDenormalizedEntity<TopicModeratorsDetailsEntity>(
                    state, TopicModeratorsDetailsSchema, topicState.moderatorsDetailsId,
                ) : null;

            return {
                topic,
                moderatorsDetails,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                loadingState: topicState.loadingState,
                isMobile: getRequestDeviceMobile(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: topicInfoFetchTrigger,
            onInviteModerator: topicInviteModeratorTrigger,
            onRemoveModerator: topicRemoveModeratorTrigger,
            onDeclineInvitation: topicModeratorInvitationDeclineTrigger,
            onErrorToast: (msg: string) => toastAdd('error', {
                component: ({ remove }) => (<Toast type="error" remove={remove}>{msg}</Toast>),
            }),
        };
    }

    public getMetaTitle(props: Props): string {
        const { topic } = props;
        if (topic) {
            return interpolate(SEO_TITLE_FORUM_TOPIC_INFO_category, { category: topic.name });
        } else {
            return SEO_TITLE_FORUM_TOPICS;
        }
    }

    public render(props: Props): false | JSX.Element {
        return (
            <TopicInfo {...props} />
        );
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { topic } = props;
        return topic
            ? topicUrl(topic)
            : forumUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        const { topic } = props;
        if (topic && topic.isCommercial) {
            return null;
        }

        return {
            zone: 'Forum Category - Halfpage Sticky (Desktop)',
        };
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { topic } = props;
        if (topic && topic.isCommercial) {
            return null;
        }

        return {
            zone: 'Forum - Branding',
        };
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const { requestUser, requestUserPermissions, topic } = props;

        if (!topic) {
            return null;
        }

        const isModerator = isAuthenticatedAs(requestUser, topic.topicModerators?.map((tm) => tm.user) || topic.moderators);
        const isAdmin = requestUserPermissions.indexOf('forum.can_manage_forum') >= 0;

        if ((isModerator || isAdmin) && !topic.deletedAt) {
            return (
                <Btn
                    type={BtnType.Blue}
                    label={FORUM_TOPIC_EDIT}
                    icon={fasPencilLg}
                    link={url(forumTopicEditUrl, {topicId: topic.id})}
                />
            );
        }
   }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer('containers.forum.forumTopicInfos', topicInfosReducer);
        store.injectSaga('containers.forum.forumTopicInfos', topicInfosSaga);
    }
}
